/**
 * NOS-239: Announcement bar context panel — edit the site-wide promo strip
 * that renders above the nav (event/ticket promos, giving campaigns, urgent
 * notices). Mirrors NavContextPanel's fixed right panel; saves through the
 * customization store's updateAnnouncement (jsonb on org_customizations).
 *
 * v1 scope: ONE bar, one link — no scheduling/rotation. Colors default from
 * brand tokens (background = primary); text color auto-adjusts for contrast.
 */
import { useEffect, useState } from "react"
import {
  parseAnnouncement,
  ANNOUNCEMENT_TEXT_MAX,
  ANNOUNCEMENT_LINK_LABEL_MAX,
  type AnnouncementConfig,
} from "@/lib/announcement"
import { TinyInput } from "./TinyInput"
import { FloatingPanel } from "./ui/FloatingPanel"
import { GROUP_LABEL } from "./ui/GroupLabel"

export interface AnnouncementPanelProps {
  announcement: Record<string, unknown> | null | undefined
  primaryColor?: string
  onUpdate: (config: Record<string, unknown> | null) => void
  onClose: () => void
}

const LABEL_STYLE = GROUP_LABEL

const HINT_STYLE: React.CSSProperties = {
  marginTop: 6,
  fontSize: 10,
  color: "rgba(255,255,255,0.3)",
}

/** Same fixed right panel geometry as NavContextPanel (spec Section 13). */
const PANEL_POSITION: React.CSSProperties = {
  top: 100,
  right: 16,
  bottom: 16,
  width: 300,
  zIndex: 1000,
}

function Toggle({
  checked,
  onChange,
  label,
  primaryColor,
}: {
  checked: boolean
  onChange: (next: boolean) => void
  label: string
  primaryColor: string
}) {
  return (
    <label
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 8,
        fontSize: 12,
        color: "rgba(255,255,255,0.75)",
        cursor: "pointer",
      }}
    >
      {label}
      <span style={{ position: "relative", flexShrink: 0, width: 34, height: 20 }}>
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          style={{
            position: "absolute",
            opacity: 0,
            inset: 0,
            width: "100%",
            height: "100%",
            margin: 0,
            cursor: "pointer",
          }}
        />
        <span
          aria-hidden
          style={{
            display: "block",
            width: 34,
            height: 20,
            borderRadius: 999,
            background: checked ? primaryColor : "rgba(255,255,255,0.12)",
            transition: "background 0.15s",
          }}
        />
        <span
          aria-hidden
          style={{
            position: "absolute",
            top: 3,
            left: checked ? 17 : 3,
            width: 14,
            height: 14,
            borderRadius: "50%",
            background: "#fff",
            transition: "left 0.15s",
            pointerEvents: "none",
          }}
        />
      </span>
    </label>
  )
}

export function AnnouncementPanel({
  announcement,
  primaryColor = "#2563eb",
  onUpdate,
  onClose,
}: AnnouncementPanelProps) {
  const config = parseAnnouncement(announcement)
  const [textDraft, setTextDraft] = useState(config?.text ?? "")
  const [linkLabelDraft, setLinkLabelDraft] = useState(config?.link_label ?? "")
  const [linkHrefDraft, setLinkHrefDraft] = useState(config?.link_href ?? "")
  useEffect(() => {
    setTextDraft(config?.text ?? "")
    setLinkLabelDraft(config?.link_label ?? "")
    setLinkHrefDraft(config?.link_href ?? "")
    // Re-sync drafts only when the stored config identity changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config?.text, config?.link_label, config?.link_href])

  /** Merge a patch over the current config and persist (empty text clears link too). */
  const save = (patch: Partial<AnnouncementConfig>) => {
    const base: AnnouncementConfig = config ?? { enabled: false, text: "", dismissible: true }
    const next: Record<string, unknown> = { ...base, ...patch }
    if (typeof next.text === "string") next.text = (next.text as string).slice(0, ANNOUNCEMENT_TEXT_MAX)
    // Drop empty link fields so the render path treats them as absent
    if (next.link_label === "") delete next.link_label
    if (next.link_href === "") delete next.link_href
    onUpdate(next)
  }

  const enabled = config?.enabled === true
  const dismissible = config?.dismissible !== false
  const background = config?.background ?? primaryColor
  const usingBrandColor = !config?.background

  return (
    <FloatingPanel
      className="animate-slideIn"
      position={PANEL_POSITION}
      title="Announcement bar"
      dotColor={primaryColor}
      onClose={onClose}
      data-testid="announcement-panel"
    >
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: 16,
          display: "flex",
          flexDirection: "column",
          gap: 18,
        }}
      >
        <div>
          <Toggle
            checked={enabled}
            onChange={(next) => save({ enabled: next })}
            label="Show announcement bar"
            primaryColor={primaryColor}
          />
          <p style={HINT_STYLE}>
            A slim strip above your menu on every page — great for events, ticket sales, and
            giving campaigns.
          </p>
        </div>

        <div>
          <label style={LABEL_STYLE}>Message</label>
          <textarea
            value={textDraft}
            maxLength={ANNOUNCEMENT_TEXT_MAX}
            onChange={(e) => setTextDraft(e.target.value)}
            onBlur={() => {
              if (textDraft.trim() !== (config?.text ?? "")) save({ text: textDraft.trim() })
            }}
            placeholder="Our gala is back — March 12. Join us!"
            rows={3}
            style={{
              width: "100%",
              boxSizing: "border-box",
              resize: "vertical",
              padding: "8px 10px",
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 8,
              color: "#fff",
              fontSize: 12,
              outline: "none",
            }}
          />
        </div>

        <div>
          <label style={LABEL_STYLE}>Link (optional)</label>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <TinyInput
              value={linkLabelDraft}
              maxLength={ANNOUNCEMENT_LINK_LABEL_MAX}
              onChange={(e) => setLinkLabelDraft(e.target.value)}
              onBlur={() => {
                if (linkLabelDraft.trim() !== (config?.link_label ?? "")) {
                  save({ link_label: linkLabelDraft.trim() })
                }
              }}
              placeholder="Get tickets"
            />
            <TinyInput
              value={linkHrefDraft}
              onChange={(e) => setLinkHrefDraft(e.target.value)}
              onBlur={() => {
                if (linkHrefDraft.trim() !== (config?.link_href ?? "")) {
                  save({ link_href: linkHrefDraft.trim() })
                }
              }}
              placeholder="/events, /donate, or https://…"
              style={{ fontSize: 11 }}
            />
          </div>
          <p style={HINT_STYLE}>
            Point it at your events, ticketing, or campaign page.
          </p>
        </div>

        <div>
          <Toggle
            checked={dismissible}
            onChange={(next) => save({ dismissible: next })}
            label="Visitors can dismiss it"
            primaryColor={primaryColor}
          />
        </div>

        <div>
          <label style={LABEL_STYLE}>Background</label>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <input
              type="color"
              aria-label="Announcement bar background color"
              value={background}
              onChange={(e) => save({ background: e.target.value })}
              style={{
                width: 34,
                height: 26,
                padding: 0,
                border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: 6,
                background: "transparent",
                cursor: "pointer",
              }}
            />
            {!usingBrandColor && (
              <button
                type="button"
                onClick={() => {
                  // Remove the override → falls back to the brand primary token
                  const base = config ?? { enabled: false, text: "", dismissible: true }
                  const next: Record<string, unknown> = { ...base }
                  delete next.background
                  onUpdate(next)
                }}
                style={{
                  padding: "5px 10px",
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: 6,
                  color: "rgba(255,255,255,0.7)",
                  fontSize: 10,
                  cursor: "pointer",
                }}
              >
                Use brand color
              </button>
            )}
          </div>
          <p style={HINT_STYLE}>
            Text color adjusts automatically so it stays readable.
          </p>
        </div>
      </div>
    </FloatingPanel>
  )
}
